import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@parentpath/i18n';
import { Moon, Clock, VolumeX, X, Check } from 'lucide-react';
import { useSleepSounds, FADE_OUT_OPTIONS, DURATION_OPTIONS } from './hooks/useSleepSounds';
import { SoundCarousel } from './components/SoundCarousel';

function formatTime(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function SleepSoundsPage() {
  const { t } = useTranslation('sleepSounds');
  const {
    sounds,
    isLoading,
    currentIndex,
    currentSound,
    isPlaying,
    selectedDurationIndex,
    setSelectedDurationIndex,
    remainingSeconds,
    fadeOutEnabled,
    setFadeOutEnabled,
    fadeOutDurationIndex,
    setFadeOutDurationIndex,
    startPlayback,
    stopPlayback,
    setCurrentIndex,
    getPublicUrl,
  } = useSleepSounds();

  const [showDurationSheet, setShowDurationSheet] = useState(false);
  const [showFadeOutSheet, setShowFadeOutSheet] = useState(false);

  const isUnlimited = DURATION_OPTIONS[selectedDurationIndex].seconds === 0;
  const playbackSeconds = DURATION_OPTIONS[selectedDurationIndex].seconds;

  // Filter fade-out options: must be less than playback duration
  const availableFadeOutOptions = FADE_OUT_OPTIONS.map((opt, i) => ({
    ...opt,
    originalIndex: i,
  })).filter((opt) => playbackSeconds > 0 && opt.seconds < playbackSeconds);

  // Duration tab text: label when idle, countdown when playing
  const durationTabText = (() => {
    if (isPlaying && remainingSeconds !== null) {
      return formatTime(remainingSeconds);
    }
    return t(`durations.${DURATION_OPTIONS[selectedDurationIndex].label}`);
  })();

  // Fade-out tab text
  const fadeOutTabText = fadeOutEnabled
    ? `${t('fadeOut.label')} · ${FADE_OUT_OPTIONS[fadeOutDurationIndex].label}`
    : t('fadeOut.label');

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 rounded-full border-2 border-white/20 border-t-pp-orange animate-spin" />
      </div>
    );
  }

  if (sounds.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 rounded-full glass-effect flex items-center justify-center mb-4">
          <Moon className="w-8 h-8 text-white/60" />
        </div>
        <h2 className="text-white text-lg font-semibold">{t('empty.title')}</h2>
        <p className="text-white/60 text-sm mt-1">{t('empty.description')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-10rem)]">
      {/* Header */}
      <h1 className="text-white text-2xl font-semibold">{t('pageTitle')}</h1>

      {/* Filter tabs */}
      <div className="flex gap-2 mt-4 self-end">
        <button
          onClick={() => setShowDurationSheet(true)}
          className="filter-tab"
          disabled={isPlaying}
        >
          <Clock className="w-3.5 h-3.5" />
          {durationTabText}
        </button>
        {!isUnlimited && (
          <button
            onClick={() => setShowFadeOutSheet(true)}
            className={`filter-tab ${fadeOutEnabled ? 'active' : ''}`}
            disabled={isPlaying}
          >
            <VolumeX className="w-3.5 h-3.5" />
            {fadeOutTabText}
          </button>
        )}
      </div>

      {/* Centered content: Carousel + Play button */}
      <div className="flex-1 flex flex-col items-center justify-center gap-6">
        <div className="-mx-4 w-[calc(100%+2rem)] flex justify-center">
          <SoundCarousel
            sounds={sounds}
            currentIndex={currentIndex}
            onIndexChange={setCurrentIndex}
            getPublicUrl={getPublicUrl}
            isPlaying={isPlaying}
          />
        </div>

        <button
          onClick={isPlaying ? stopPlayback : startPlayback}
          disabled={!currentSound}
          className={`w-full max-w-[200px] py-3 rounded-2xl font-semibold text-white text-base transition-all ${
            isPlaying
              ? 'bg-red-500 hover:bg-red-600 active:bg-red-700'
              : 'bg-pp-orange hover:bg-pp-orange/90 active:bg-pp-orange/80'
          } disabled:opacity-40 disabled:pointer-events-none`}
        >
          {isPlaying ? t('player.stop') : t('player.play')}
        </button>
      </div>

      {/* Duration Bottom Sheet */}
      {showDurationSheet &&
        createPortal(
          <>
            <div
              className="bottom-sheet-backdrop"
              onClick={() => setShowDurationSheet(false)}
            />
            <div className="bottom-sheet-container">
              <div className="bottom-sheet-modal">
                <div className="bottom-sheet-header">
                  <h2>{t('player.selectDuration')}</h2>
                  <button
                    onClick={() => setShowDurationSheet(false)}
                    className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="bottom-sheet-content space-y-1">
                  {DURATION_OPTIONS.map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        setSelectedDurationIndex(i);
                        setShowDurationSheet(false);
                      }}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                        selectedDurationIndex === i
                          ? 'bg-pp-orange/20 text-pp-orange'
                          : 'text-white hover:bg-white/10'
                      }`}
                    >
                      <span className="font-medium">
                        {t(`durations.${opt.label}`)}
                      </span>
                      {selectedDurationIndex === i && (
                        <Check className="w-5 h-5" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}

      {/* Fade-Out Bottom Sheet */}
      {showFadeOutSheet &&
        createPortal(
          <>
            <div
              className="bottom-sheet-backdrop"
              onClick={() => setShowFadeOutSheet(false)}
            />
            <div className="bottom-sheet-container">
              <div className="bottom-sheet-modal">
                <div className="bottom-sheet-header">
                  <h2>{t('fadeOut.label')}</h2>
                  <button
                    onClick={() => setShowFadeOutSheet(false)}
                    className="w-8 h-8 rounded-[5px] bg-white/20 flex items-center justify-center text-white hover:bg-white/30"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="bottom-sheet-content space-y-4">
                  {/* Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-white font-medium">
                      {t('fadeOut.enable')}
                    </span>
                    <button
                      onClick={() => setFadeOutEnabled(!fadeOutEnabled)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        fadeOutEnabled ? 'bg-pp-orange' : 'bg-white/20'
                      }`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          fadeOutEnabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Duration options */}
                  {fadeOutEnabled && availableFadeOutOptions.length > 0 && (
                    <div className="space-y-1">
                      <p className="text-white/60 text-sm mb-2">
                        {t('fadeOut.duration')}
                      </p>
                      {availableFadeOutOptions.map((opt) => (
                        <button
                          key={opt.originalIndex}
                          onClick={() =>
                            setFadeOutDurationIndex(opt.originalIndex)
                          }
                          className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-colors ${
                            fadeOutDurationIndex === opt.originalIndex
                              ? 'bg-pp-orange/20 text-pp-orange'
                              : 'text-white hover:bg-white/10'
                          }`}
                        >
                          <span className="font-medium">{opt.label}</span>
                          {fadeOutDurationIndex === opt.originalIndex && (
                            <Check className="w-5 h-5" />
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
}
